import type { SimDailyResponse, SimDailyResults } from '@cruz/shared';
import { useEffect, useRef, useState } from 'react';
import { load } from '../lib/storage.ts';
import { navigate } from '../challenge/router.ts';
import { simApi, simErrorMessage } from './api.ts';
import { PlayCta, VOTER_KEY, voterId } from './Share.tsx';
import { Board, Intro, mistakes, seconds } from './TramoBoard.tsx';
import { card, Notice, primaryButton, secondaryButton, Working } from './ui.tsx';

/*
 * «El tramo del día», at /simulador/tramo: the same tramo for everyone, once a day and
 * without an account. The result goes to WhatsApp as a line of squares, and from here
 * whoever does not play yet can start a career.
 */

type Phase =
  | { at: 'loading' }
  | { at: 'error'; message: string }
  | { at: 'intro' | 'play'; daily: SimDailyResponse }
  | { at: 'sending' }
  | { at: 'done'; results: SimDailyResults };

export function DailyPage({
  onPlay,
  hasCareer,
}: {
  /** Start a career as a guest */
  onPlay: () => Promise<void>;
  /** Signed in with a career: back to it instead of starting one */
  hasCareer: boolean;
}) {
  const [phase, setPhase] = useState<Phase>({ at: 'loading' });
  useEffect(() => {
    let alive = true;
    simApi
      .daily(load<string | null>(VOTER_KEY, null))
      .then((d) => {
        if (!alive) return;
        setPhase(d.results ? { at: 'done', results: d.results } : { at: 'intro', daily: d });
      })
      .catch((err: unknown) => alive && setPhase({ at: 'error', message: simErrorMessage(err) }));
    return () => {
      alive = false;
    };
  }, []);

  const finish = async (daily: SimDailyResponse, taps: string[], ms: number) => {
    setPhase({ at: 'sending' });
    try {
      const results = await simApi.playDaily(daily.day, taps, ms, voterId());
      setPhase({ at: 'done', results });
      scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setPhase({ at: 'error', message: simErrorMessage(err) });
    }
  };

  switch (phase.at) {
    case 'loading':
      return <Working label="Reuniendo al tramo…" />;
    case 'sending':
      return <Working label="Contando los nazarenos…" />;
    case 'error':
      return (
        <div className="space-y-3">
          <Notice tone="error">{phase.message}</Notice>
          <button type="button" className={secondaryButton} onClick={() => location.reload()}>
            Volver a probar
          </button>
        </div>
      );
    case 'intro': {
      const { daily } = phase;
      const puzzle = daily.puzzle!;
      return (
        <div className="space-y-4">
          <Intro
            puzzle={puzzle}
            kicker={`Nº ${daily.number} · Nivel ${puzzle.level}`}
            note={`Tienes ${puzzle.seconds} segundos y un solo intento. Mañana, otro.`}
            onStart={() => {
              setPhase({ at: 'play', daily });
              scrollTo({ top: 0 });
            }}
          />
          <p className="text-ink-soft dark:text-cream/75">
            Sin registrarte. El tramo es el mismo para todo el mundo durante el día: así podéis
            compararlo en el grupo.
          </p>
        </div>
      );
    }
    case 'play': {
      const { daily } = phase;
      return <Board puzzle={daily.puzzle!} onFinish={(taps, ms) => void finish(daily, taps, ms)} />;
    }
    case 'done':
      return <Results results={phase.results} onPlay={onPlay} hasCareer={hasCareer} />;
  }
}

const TITLES: Record<number, (complete: boolean) => string> = {
  3: () => '¡Tramo perfecto!',
  2: () => 'Buen tramo',
  1: (complete) => (complete ? 'Tramo formado, con tropiezos' : 'Se acabó el tiempo'),
};

/** The text that goes to WhatsApp: the result without giving the tramo away. */
export function dailyShareText(r: SimDailyResults, url: string): string {
  const stars = `${'★'.repeat(r.mine.stars)}${'☆'.repeat(3 - r.mine.stars)}`;
  const time = r.mine.complete ? `${seconds(r.mine.ms)} s` : 'sin terminar';
  return `El tramo del día nº ${r.number} · ${stars}\n${time} · ${mistakes(r.mine.errors)}\n${r.grid}\n${url}`;
}

function Results({
  results: r,
  onPlay,
  hasCareer,
}: {
  results: SimDailyResults;
  onPlay: () => Promise<void>;
  hasCareer: boolean;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  const [copied, setCopied] = useState(false);
  const text = dailyShareText(r, `${location.origin}/simulador/tramo`);
  const first = [...r.grid].filter((c) => c === '🟣').length;
  const total = [...r.grid].length;
  const canShare = typeof navigator.share === 'function';
  const share = async () => {
    try {
      await navigator.share({ text });
    } catch {
      // Cancelled
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <article aria-labelledby="daily-result" className="space-y-4">
      <div className="animate-crown rounded-2xl border-2 border-gold bg-purple px-5 py-6 text-center text-cream dark:bg-night-700">
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-300 uppercase">
          El tramo del día nº {r.number}
        </p>
        <p
          className="mt-2 text-4xl tracking-[0.2em] text-gold-300"
          role="img"
          aria-label={`${r.mine.stars} de 3 estrellas`}
        >
          {'★'.repeat(r.mine.stars)}
          <span className="opacity-30">{'★'.repeat(3 - r.mine.stars)}</span>
        </p>
        <h1
          id="daily-result"
          ref={titleRef}
          tabIndex={-1}
          className="mt-2 font-serif text-[2rem] leading-tight font-semibold outline-none"
        >
          {TITLES[r.mine.stars]!(r.mine.complete)}
        </h1>
        <p className="mt-2 text-cream/85">
          {r.mine.complete ? `${seconds(r.mine.ms)} s` : 'Sin terminar'} · {mistakes(r.mine.errors)}
        </p>
        <p
          className="mt-3 text-2xl tracking-[0.15em]"
          role="img"
          aria-label={`${first} de ${total} en su sitio a la primera`}
        >
          {r.grid}
        </p>
      </div>
      <div className={`${card} space-y-1 px-5 py-4 text-[1.05rem]`}>
        <p>
          {r.players === 1
            ? 'Eres la primera persona en jugarlo hoy.'
            : `Hoy lo han jugado ${r.players.toLocaleString('es-ES')} personas; el ${r.perfect_pct} %, sin un fallo.`}
        </p>
        {r.beat_pct !== null && (
          <p className="font-semibold">Lo has hecho mejor que el {r.beat_pct} % de los demás.</p>
        )}
      </div>
      <div className="space-y-3">
        {canShare && (
          <button type="button" className={primaryButton} onClick={() => void share()}>
            Compartir mi resultado
          </button>
        )}
        <a
          className={canShare ? secondaryButton : primaryButton}
          href={`https://wa.me/?text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Enviar por WhatsApp
        </a>
        <button type="button" className={secondaryButton} onClick={() => void copy()}>
          {copied ? '¡Resultado copiado!' : 'Copiar el resultado'}
        </button>
      </div>
      <p className="text-center text-ink-soft dark:text-cream/75">Mañana a las 6:00, otro tramo.</p>
      <div className="space-y-3 border-t border-gold/40 pt-5">
        {hasCareer ? (
          <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
            Volver a mi hermandad
          </button>
        ) : (
          <>
            <p className="text-lg">
              ¿Te ha gustado? En <em>De cirio a hermano mayor</em> fundas tu hermandad y empiezas de
              cirio, en el primer tramo. Un rato cada día.
            </p>
            <PlayCta onPlay={onPlay} />
          </>
        )}
      </div>
    </article>
  );
}
